import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

class PDFService {
    constructor() {
        this.pageWidth = 210; // A4 width in mm
        this.pageHeight = 297; // A4 height in mm
        this.margin = 12; // Increased margin for print compatibility
        this.contentWidth = this.pageWidth - (this.margin * 2);
        this.logoBase64 = null; // Cache for logo
        
        // Print optimization settings
        this.printMargin = {
            top: 15,
            bottom: 20,
            left: 12,
            right: 12
        };
    }

    /**
     * Load and convert logo to base64 for PDF use
     */
    async loadLogo() {
        if (this.logoBase64) return this.logoBase64;

        try {
            const response = await fetch('/admin/assets/images/logo.png');
            const blob = await response.blob();

            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => {
                    this.logoBase64 = reader.result;
                    resolve(this.logoBase64);
                };
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.log('Could not load logo for PDF');
            return null;
        }
    }

    /**
     * Format date for PDF display
     */
    formatDateForPDF(dateString) {
        if (!dateString) return '';

        try {
            // Handle different date formats that might come from backend
            let date;

            // If it's already a Date object
            if (dateString instanceof Date) {
                date = dateString;
            }
            // If it's a string in YYYY-MM-DD format
            else if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
                date = new Date(dateString + 'T00:00:00');
            }
            // If it's a string in DD/MM/YYYY or DD/M/YYYY format (from legacy backend)
            else if (typeof dateString === 'string' && dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
                const parts = dateString.split('/');
                date = new Date(parts[2], parts[1] - 1, parts[0]);
            }
            // Try parsing as is
            else {
                date = new Date(dateString);
            }

            // Check if date is valid
            if (isNaN(date.getTime())) {
                console.warn('Invalid date format for PDF:', dateString);
                return dateString; // Return original string if parsing fails
            }

            // Format for PDF: DD/MM/YYYY
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        } catch (error) {
            console.error('Error formatting date for PDF:', dateString, error);
            return dateString || '';
        }
    }

    /**
     * Generate professional multi-page invoice PDF with creative layout
     */
    async generateLegacyInvoicePDF(invoiceData) {
        const { invoice_info, customer_info, summary_data, booking_details, company_info } = invoiceData;

        // Load logo first
        await this.loadLogo();

        // Create new PDF document with print optimization
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            putOnlyUsedFonts: true,
            floatPrecision: 16 // Higher precision for better print quality
        });

        // Set print-friendly properties
        pdf.setFont('helvetica');
        pdf.setCreationDate(new Date());
        pdf.setProperties({
            title: `SunEx Invoice ${invoiceData.invoice_info?.invoice_no || ''}`,
            subject: 'Courier Service Invoice',
            author: 'SUN-EX COURIER, CARGO & LOGISTICS PVT. LTD.',
            creator: 'SunEx Invoice System',
            producer: 'jsPDF'
        });

        // PAGE 1: COMPREHENSIVE INVOICE OVERVIEW
        // Ensure all first page content stays together
        pdf.setPage(0); // Make sure we're on the first page
        this.addInvoiceHeaderPage(pdf, invoiceData);

        // PAGE 2+: DETAILED PAGES FOR EACH PAYMENT MODE WITH PAGINATION
        if (booking_details && Object.keys(booking_details).length > 0) {
            let pageNumber = 2; // Start from page 2 (after header page)

            Object.entries(booking_details).forEach(([paymentMode, bookings], paymentModeIndex) => {
                // Smart pagination: 7 records per page, but handle exactly 7 records specially
                let bookingChunks = [];
                const totalRecords = bookings.length;
                
                if (totalRecords <= 6) {
                    // 6 or fewer records - all on one page with summary
                    bookingChunks = [bookings];
                } else if (totalRecords === 7) {
                    // Special case: exactly 7 records - split into 6 + 1 for summary space
                    bookingChunks = [
                        bookings.slice(0, 6),  // First 6 records
                        bookings.slice(6, 7)   // Last 1 record (will have summary)
                    ];
                } else {
                    // More than 7 records - smart pagination to ensure last page has ≤ 6 records
                    const recordsPerPage = 7;
                    const fullPages = Math.floor(totalRecords / recordsPerPage);
                    const remainingRecords = totalRecords % recordsPerPage;
                    
                    bookingChunks = [];
                    
                    if (remainingRecords === 0) {
                        // Perfect division (e.g., 14, 21, 28, 35, etc.)
                        // Need to ensure last page has ≤ 6 records
                        const adjustedFullPages = fullPages - 1; // Reduce by 1 to leave space for summary
                        
                        // Add full pages (7 records each)
                        for (let i = 0; i < adjustedFullPages; i++) {
                            bookingChunks.push(bookings.slice(i * recordsPerPage, (i + 1) * recordsPerPage));
                        }
                        
                        // Add second-to-last page with 6 records
                        const secondLastPageStart = adjustedFullPages * recordsPerPage;
                        bookingChunks.push(bookings.slice(secondLastPageStart, secondLastPageStart + 6));
                        
                        // Add last page with remaining records
                        const lastPageStart = secondLastPageStart + 6;
                        const remainingInLastPage = totalRecords - lastPageStart;
                        if (remainingInLastPage > 0) {
                            bookingChunks.push(bookings.slice(lastPageStart));
                        }
                    } else {
                        // Has remaining records (e.g., 8, 9, 15, 16, 22, 23, etc.)
                        
                        // Add full pages
                        for (let i = 0; i < fullPages; i++) {
                            bookingChunks.push(bookings.slice(i * recordsPerPage, (i + 1) * recordsPerPage));
                        }
                        
                        // Handle remaining records
                        if (remainingRecords <= 6) {
                            // 6 or fewer remaining - put them on last page with summary
                            bookingChunks.push(bookings.slice(fullPages * recordsPerPage));
                        } else {
                            // Exactly 7 remaining - split into 6 + 1
                            const lastFullPage = bookings.slice(fullPages * recordsPerPage, (fullPages + 1) * recordsPerPage);
                            const finalRecords = bookings.slice((fullPages + 1) * recordsPerPage);
                            bookingChunks.push(lastFullPage);
                            bookingChunks.push(finalRecords);
                        }
                    }
                }
                
                const totalChunks = bookingChunks.length;
                
                bookingChunks.forEach((bookingChunk, chunkIndex) => {
                    // Add new page for each chunk (ALL payment mode detail pages should be on separate pages)
                    if (paymentModeIndex > 0 || chunkIndex > 0) {
                        pdf.addPage();
                        pageNumber++;
                    } else {
                        // For the very first chunk of the first payment mode, also add a new page
                        pdf.addPage();
                        pageNumber++;
                    }
                    
                    const isLastChunk = chunkIndex === totalChunks - 1;
                    this.addPaymentModeDetailPage(pdf, paymentMode, bookingChunk, invoiceData, pageNumber, {
                        isLastChunk,
                        chunkIndex,
                        totalChunks,
                        totalRecords: bookings.length,
                        recordsInThisChunk: bookingChunk.length
                    });
                });
            });
        }
        
        // Add page numbers to all pages
        this.addPageNumbers(pdf);
        
        return pdf;
    }
    
    /**
     * PAGE 1: Comprehensive Invoice Header Page
     */
    addInvoiceHeaderPage(pdf, invoiceData) {
        const { invoice_info, customer_info, summary_data, company_info } = invoiceData;
        let yPos = this.margin;

        // 1. PROFESSIONAL HEADER WITH LOGO AND COMPANY INFO
        yPos = this.addProfessionalHeader(pdf, company_info, yPos);

        // 2. INVOICE TITLE SECTION
        yPos = this.addInvoiceTitleSection(pdf, invoice_info, yPos);

        // 3. TWO-COLUMN LAYOUT: COMPANY ADDRESS & CUSTOMER ADDRESS
        yPos = this.addAddressSection(pdf, company_info, customer_info, yPos);

        // 4. INVOICE DETAILS BOX
        yPos = this.addInvoiceDetailsBox(pdf, invoiceData, yPos);

        // 5. PAYMENT MODE SUMMARY TABLE
        if (summary_data && Object.keys(summary_data).length > 0) {
            yPos = this.addPaymentModeSummaryTable(pdf, summary_data, yPos);
        }

        // 6. TERMS AND CONDITIONS
        yPos = this.addTermsAndConditionsSection(pdf, company_info, yPos);

        // 7. BANK DETAILS SECTION
        yPos = this.addBankDetailsSection(pdf, yPos);

        // Check if content exceeds page height and add page break if needed
        if (yPos > this.pageHeight - 30) { // Leave space for footer
            pdf.addPage();
            yPos = this.margin;
        }

        // 8. FOOTER NOTE
        this.addFooterNote(pdf, 1);
    }
    
    /**
     * Professional Header with Logo and Branding
     */
    addProfessionalHeader(pdf, company_info, yPos) {
        // Company Logo (if available)
        if (this.logoBase64) {
            try {
                const logoWidth = 60;
                const logoHeight = 22; // Reduced from 25
                pdf.addImage(this.logoBase64, 'PNG', this.margin, yPos, logoWidth, logoHeight);
            } catch (error) {
                // Logo error silently ignored
            }
        }
        
        // Company Name and Tagline (Right side)
        const companyX = this.pageWidth - this.margin - 80;
        pdf.setFontSize(15); // Reduced from 16
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 102, 204); // Blue color
        
        const companyName = company_info?.name || 'SUN-EX COURIER, CARGO & LOGISTICS PVT. LTD.';
        const companyLines = pdf.splitTextToSize(companyName, 80);
        
        let textY = yPos + 6; // Reduced from 8
        companyLines.forEach(line => {
            pdf.text(line, companyX, textY);
            textY += 5; // Reduced from 6
        });
        
        // Tagline
        pdf.setFontSize(9); // Reduced from 10
        pdf.setFont('helvetica', 'italic');
        pdf.setTextColor(100, 100, 100);
        pdf.text('Express Delivery Solutions', companyX, textY + 1); // Reduced from +2
        
        // Reset text color
        pdf.setTextColor(0, 0, 0);
        
        return yPos + 30; // Reduced from 35
    }
    
    /**
     * Invoice Title Section
     */
    addInvoiceTitleSection(pdf, invoice_info, yPos) {
        // Centered INVOICE title with decorative elements
        pdf.setFontSize(22); // Reduced from 24
        pdf.setFont('helvetica', 'bold');
        
        const title = 'INVOICE';
        const titleWidth = pdf.getTextWidth(title);
        const centerX = this.pageWidth / 2;
        
        // Add background rectangle for title
        pdf.setFillColor(240, 248, 255);
        pdf.rect(centerX - (titleWidth / 2) - 10, yPos - 2, titleWidth + 20, 10, 'F'); // Reduced height from 12
        
        // Add border
        pdf.setLineWidth(0.5);
        pdf.setDrawColor(0, 102, 204);
        pdf.rect(centerX - (titleWidth / 2) - 10, yPos - 2, titleWidth + 20, 10); // Reduced height from 12
        
        // Title text
        pdf.setTextColor(0, 102, 204);
        pdf.text(title, centerX - (titleWidth / 2), yPos + 5); // Reduced from +6
        
        // Subtitle
        pdf.setFontSize(11); // Reduced from 12
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        const subtitle = 'Freight & Courier Services';
        const subtitleWidth = pdf.getTextWidth(subtitle);
        pdf.text(subtitle, centerX - (subtitleWidth / 2), yPos + 13); // Reduced from +15
        
        // Reset colors
        pdf.setTextColor(0, 0, 0);
        pdf.setDrawColor(0, 0, 0);
        
        return yPos + 22; // Reduced from 25
    }
    
    /**
     * Address Section - Two Column Layout
     */
    addAddressSection(pdf, company_info, customer_info, yPos) {
        const colWidth = (this.contentWidth - 10) / 2;
        const leftCol = this.margin;
        const rightCol = this.margin + colWidth + 10;
        
        // COMPANY ADDRESS (Left Column)
        pdf.setFontSize(11); // Reduced from 12
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 102, 204);
        // Push the label slightly down so it is not intersected by the border
        pdf.text('FROM:', leftCol, yPos + 6);
        
        pdf.setFontSize(9); // Reduced from 10
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        
        // Spacing configuration
        const lineSpacing = 4.5; // Comfortable line spacing for readability
        // Add more spacing under the section title for readability
        let addressY = yPos + 12;
        
        // Company details
        const companyName = company_info?.name || 'SUN-EX COURIER, CARGO & LOGISTICS PVT. LTD.';
        const address = company_info?.head_office?.address || 
            'NO.28/9, Ambiga complex, 1st East Main Road, Gandhi Nagar, Vellore - 632006';
        const state = company_info?.head_office?.state || 'Tamil Nadu, India';
        const contact = company_info?.head_office?.contact || 
            'Contact: (0416) 2249109 / 3552615, +91 90430 18900';
        const email = 'Mail ID: sunexcourier2010@gmail.com';
        const gst = 'GST NO: 33AAOCS4194A1ZE';
        
        const companyLines = pdf.splitTextToSize(companyName, colWidth - 5);
        companyLines.forEach(line => {
            pdf.text(line, leftCol, addressY);
            addressY += lineSpacing;
        });
        
        const addressLines = pdf.splitTextToSize(address, colWidth - 5);
        addressLines.forEach(line => {
            pdf.text(line, leftCol, addressY);
            addressY += lineSpacing;
        });
        
        pdf.text(state, leftCol, addressY);
        addressY += lineSpacing;
        pdf.text(contact, leftCol, addressY);
        addressY += lineSpacing;
        pdf.text(email, leftCol, addressY);
        addressY += lineSpacing;
        pdf.text(gst, leftCol, addressY);
        const leftEndY = addressY;
        
        // CUSTOMER ADDRESS (Right Column)
        pdf.setFontSize(11); // Reduced from 12
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 102, 204);
        // Push the label slightly down so it is not intersected by the border
        pdf.text('BILL TO:', rightCol, yPos + 6);
        
        pdf.setFontSize(9); // Reduced from 10
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        
        // Reset for right column with same spacing
        addressY = yPos + 12;
        
        // Use billing customer information if available, otherwise fall back to customer_info
        let customerText;
        if (customer_info?.billing_customer) {
            customerText = this.formatBillingCustomerAddress(customer_info.billing_customer);
        } else {
            customerText = this.parseCustomerData(customer_info?.customer_data_inv);
        }
        
        const customerLines = customerText.split('\n').filter(line => line.trim());
        
        customerLines.forEach(line => {
            if (line.trim()) {
                const lineData = pdf.splitTextToSize(line.trim(), colWidth - 5);
                lineData.forEach(lineItem => {
                    pdf.text(lineItem, rightCol, addressY);
                    addressY += lineSpacing;
                });
            }
        });
        const rightEndY = addressY;
        
        // Add borders around addresses with top/bottom padding
        const rectTop = yPos + 2; // Start box a bit below the initial y to avoid cutting labels
        const contentBottom = Math.max(leftEndY, rightEndY);
        const maxHeight = Math.max((contentBottom - rectTop) + 6, 50); // Bottom padding and minimum height
        pdf.setLineWidth(0.3);
        pdf.setDrawColor(200, 200, 200);
        pdf.rect(leftCol - 2, rectTop - 2, colWidth, maxHeight);
        pdf.rect(rightCol - 2, rectTop - 2, colWidth, maxHeight);
        
        pdf.setDrawColor(0, 0, 0);
        pdf.setTextColor(0, 0, 0);
        
        return rectTop + maxHeight + 8; // Maintain spacing after the boxes
    }

    /**
     * Format billing customer address
     */
    formatBillingCustomerAddress(billingCustomer) {
        if (!billingCustomer) return 'Customer information not available';
        
        let address = '';
        
        // [Customer Name] - contact person name
        if (billingCustomer.name && billingCustomer.name.trim()) {
            address += billingCustomer.name;
        }
        
        // [Customer Company] - company name
        if (billingCustomer.customer_name && billingCustomer.customer_name.trim()) {
            address += '\n' + billingCustomer.customer_name;
        }
        
        // [Division Name] - division name
        if (
            billingCustomer.customer_name !== billingCustomer.company
            && billingCustomer.company !== 'NA'
            && billingCustomer.company && billingCustomer.company.trim()
        ) {
            address += '\n' + billingCustomer.company;
        }
        
        // [Customer Address]
        if (billingCustomer.address && billingCustomer.address.trim()) {
            address += '\n' + billingCustomer.address;
        }
        
        // [City - Pincode]
        let cityPincode = '';
        if (billingCustomer.city && billingCustomer.city.trim()) {
            cityPincode += billingCustomer.city;
        }
        
        if (billingCustomer.pincode && billingCustomer.pincode.trim()) {
            cityPincode += (cityPincode ? ' - ' : '') + billingCustomer.pincode;
        }
        
        if (cityPincode) {
            address += '\n' + cityPincode;
        }
        
        // [State]
        if (billingCustomer.state && billingCustomer.state.trim()) {
            address += '\n' + billingCustomer.state;
        }
        
        // Contact: [Customer Mobile]
        if (billingCustomer.mobile && billingCustomer.mobile.trim()) {
            address += '\nContact: ' + billingCustomer.mobile;
        }
        
        // Email: [Customer Email]
        if (billingCustomer.email && billingCustomer.email.trim()) {
            address += '\nEmail: ' + billingCustomer.email;
        }
        
        // GSTIN: [Customer GST]
        const gstValue = billingCustomer.gst && String(billingCustomer.gst).trim();
        if (gstValue) {
            address += '\nGSTIN: ' + gstValue;
        }
        
        return address;
    }
    
    /**
     * Invoice Details Box
     */
    addInvoiceDetailsBox(pdf, invoiceData, yPos) {
        const { invoice_info, customer_info } = invoiceData;
        const boxWidth = this.contentWidth;
        const boxHeight = 22; // Reduced from 25
        
        // Box background
        pdf.setFillColor(248, 249, 250);
        pdf.rect(this.margin, yPos, boxWidth, boxHeight, 'F');
        
        // Box border
        pdf.setLineWidth(0.5);
        pdf.setDrawColor(0, 102, 204);
        pdf.rect(this.margin, yPos, boxWidth, boxHeight);
        
        // Invoice details in table format
        const details = [
            ['Account Number:', customer_info?.cust_code || 'N/A'],
            ['Invoice Number:', invoice_info?.invoice_no || ''],
            ['Invoice Date:', this.formatDateForPDF(invoice_info?.invoice_date) || ''],
            ['Due Date:', this.formatDateForPDF(invoice_info?.due_date) || ''],
            ['SAC Code:', '9965'],
            ['Reverse Charge:', invoice_info?.reverse_charge === 'yes' ? 'Yes' : 'No'],
            ['Currency:', 'INR'],
            ['Total Amount:', this.formatCurrency(Math.round(invoice_info?.grand_total || 0))]
        ];
        
        pdf.setFontSize(9); // Reduced from 10
        pdf.setFont('helvetica', 'normal');
        
        let detailY = yPos + 5; // Reduced from 6
        const leftColX = this.margin + 5;
        const rightColX = this.margin + (boxWidth / 2) + 5;
        const colWidth = (boxWidth / 2) - 10;
        
        details.forEach((detail, index) => {
            const col = index % 2 === 0 ? leftColX : rightColX;
            const lineY = detailY + Math.floor(index / 2) * 5; // Reduced from 6
            
            pdf.setFont('helvetica', 'bold');
            pdf.text(detail[0], col, lineY);
            pdf.setFont('helvetica', 'normal');
            
            if (index === details.length - 1) { // Total amount
                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(11); // Reduced from 12
            }
            
            pdf.text(detail[1], col + 40, lineY);
            pdf.setFontSize(9); // Reduced from 10
        });
        
        pdf.setDrawColor(0, 0, 0);
        return yPos + boxHeight + 10; // Reduced from 15
    }

    /**
     * Bank Details and Authorized Signature Section
     */
    addBankDetailsSection(pdf, yPos) {
        const sectionWidth = (this.contentWidth - 10) / 2; // Split into two halves with gap
        const leftCol = this.margin;
        const rightCol = this.margin + sectionWidth + 10;

        // Left side: Bank Details
        pdf.setFontSize(8); // Further reduced from 9 to balance with larger payment summary
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 102, 204);
        pdf.text('BANK DETAILS FOR PAYMENT', leftCol, yPos);

        let bankY = yPos + 4; // Further reduced from 5

        const bankDetailsData = [
            [
                'Payable to: Sun-ex Courier Cargo & Logistics Pvt Ltd'
            ],
            [
                'Bank: Indian Bank, VIT Campus, Vellore - 632014'
            ],
            [
                'Account No: 6853116104'
            ],
            [
                'IFSC: IDIB000V086'
            ]
        ];

        autoTable(pdf, {
            body: bankDetailsData,
            startY: bankY,
            styles: {
                fontSize: 6, // Further reduced from 7
                cellPadding: 0.8, // Further reduced from 1
                lineColor: [200, 200, 200],
                lineWidth: 0.3,
                overflow: 'linebreak'
            },
            columnStyles: {
                0: { cellWidth: sectionWidth, halign: 'left' }
            },
            tableWidth: sectionWidth,
            margin: { left: leftCol, right: leftCol + sectionWidth }
        });

        // Right side: Authorized Signature Section
        let signatureY = yPos;

        // Add space for seal and signature (35mm height rectangle - reduced)
        const signatureBoxHeight = 35; // Reduced from 40
        const signatureBoxY = signatureY - 2;

        // Draw signature box
        pdf.setLineWidth(0.5);
        pdf.setDrawColor(0, 102, 204);
        pdf.rect(rightCol, signatureBoxY, sectionWidth, signatureBoxHeight);

        // Add authorized signature text at the bottom of the box
        pdf.setFontSize(8); // Further reduced from 9 to balance with larger payment summary
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        const signatureText = 'AUTHORIZED SIGNATORY';
        const signatureWidth = pdf.getTextWidth(signatureText);
        const signatureX = rightCol + (sectionWidth / 2) - (signatureWidth / 2);
        pdf.text(signatureText, signatureX, signatureBoxY + signatureBoxHeight - 9); // Further reduced from -10

        // Add company name at the very bottom of the box
        pdf.setFontSize(7); // Further reduced from 8 to balance
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        const companyName = 'SUN-EX COURIER, CARGO & LOGISTICS PVT. LTD.';
        const companyLines = pdf.splitTextToSize(companyName, sectionWidth - 10);

        // Position company name at the bottom (with reduced padding)
        let companyY = signatureBoxY + signatureBoxHeight - 4; // Further reduced from -5

        // If multiple lines, position from bottom up
        for (let i = companyLines.length - 1; i >= 0; i--) {
            const line = companyLines[i];
            const lineWidth = pdf.getTextWidth(line);
            const lineX = rightCol + (sectionWidth / 2) - (lineWidth / 2);
            pdf.text(line, lineX, companyY);
            companyY -= 3; // Reduced spacing from 4
        }

        // Calculate the maximum Y position from both sections
        const bankTableEndY = pdf.lastAutoTable.finalY;
        const signatureEndY = signatureBoxY + signatureBoxHeight;

        pdf.setTextColor(0, 0, 0);
        pdf.setDrawColor(0, 0, 0);

        return Math.max(bankTableEndY, signatureEndY) + 6; // Further reduced from +6 to save space
    }
    
    /**
     * Payment Mode Summary Table
     */
    addPaymentModeSummaryTable(pdf, summary_data, yPos) {
        pdf.setFontSize(12); // Increased from 10 - primary content
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 102, 204);
        pdf.text('PAYMENT MODE SUMMARY', this.margin, yPos);

        yPos += 6; // Increased from 5 - more spacing for primary content
        
        const headers = [
            'Payment Mode',
            'Shipments',
            'Freight (Rs.)',
            'Fuel Surcharge (Rs.)',
            'Discount (Rs.)',
            'SGST 9% (Rs.)',
            'CGST 9% (Rs.)',
            'Total Amount (Rs.)'
        ];
        
        const tableData = [];
        Object.entries(summary_data).forEach(([mode, data]) => {
            // Split GST into SGST 9% and CGST 9% (each half of total service tax)
            const totalGST = data.totalService || 0;
            const sgst = totalGST / 2;
            const cgst = totalGST / 2;

            // Combine freight and other charges
            const combinedFreight = (data.totalFreight || 0) + (data.other_charges || 0);

            tableData.push([
                data.title || mode,
                data.shipmentCount.toString(),
                this.formatCurrency(combinedFreight),
                this.formatCurrency(data.fuel_surcharge),
                this.formatCurrency(data.discount || 0),
                this.formatCurrency(sgst),
                this.formatCurrency(cgst),
                this.formatCurrency(data.totalAmount)
            ]);
        });
        
                autoTable(pdf, {
            head: [headers],
            body: tableData,
            startY: yPos,
            styles: {
                fontSize: 8, // Increased from 5 - primary content needs larger font
                cellPadding: 1, // Increased from 0.5 - better readability for primary content
                lineColor: [200, 200, 200],
                lineWidth: 0.3,
                overflow: 'linebreak'
            },
            headStyles: {
                fillColor: [0, 102, 204],
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                fontSize: 9 // Increased from 6 - prominent header for primary content
            },
            alternateRowStyles: {
                fillColor: [248, 249, 250]
            },
            columnStyles: {
                0: { cellWidth: 32, halign: 'left' },     // Payment Mode = 32mm (increased)
                1: { cellWidth: 16, halign: 'center' },   // Shipments = 16mm (increased)
                2: { cellWidth: 24, halign: 'right' },    // Freight (includes other charges) = 24mm (increased)
                3: { cellWidth: 22, halign: 'right' },    // Fuel Surcharge = 22mm (increased)
                4: { cellWidth: 18, halign: 'right' },    // Discount = 18mm (increased)
                5: { cellWidth: 18, halign: 'right' },    // SGST 9% = 18mm (increased)
                6: { cellWidth: 18, halign: 'right' },    // CGST 9% = 18mm (increased)
                7: { cellWidth: 32, halign: 'right', fontStyle: 'bold' }  // Total = 32mm (increased)
            },
            // Total = 32+16+24+22+18+18+18+32 = 180mm (within contentWidth of 186mm)
            tableWidth: this.contentWidth,
            margin: { left: this.margin, right: this.margin }
        });

        pdf.setTextColor(0, 0, 0);
        return pdf.lastAutoTable.finalY + 10; // Increased from 8 - more spacing after primary content
    }
    
    /**
     * Terms and Conditions Section
     */
    addTermsAndConditionsSection(pdf, company_info, yPos) {
                pdf.setFontSize(9); // Further reduced from 10 to balance with larger payment summary
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 102, 204);
        pdf.text('TERMS & CONDITIONS', this.margin, yPos);

        yPos += 4; // Further reduced from 5 to save space

        const termsData = [
            [
                '1. Kindly revert back in writing regarding any query pertaining to this invoice within "SEVEN DAYS" from the date of the invoice. Otherwise this invoice shall be deemed to be correct.'
            ],
            [
                '2. Interest @ 1.5% is chargeable for payment after Due Date.'
            ],
            [
                '3. We calculated your charges based on dimensional weight. For further information, please refer Conditions of Carriage on sun-excourier.com'
            ],
            [
                '4. For any further assistance call us on (0416) 2249109 (OR) +91-90430 18900'
            ]
        ];

        autoTable(pdf, {
            body: termsData,
            startY: yPos,
            styles: {
                fontSize: 6, // Further reduced from 7 to save space
                cellPadding: 0.8, // Further reduced from 1 to save space
                lineColor: [200, 200, 200],
                lineWidth: 0.3,
                overflow: 'linebreak'
            },
            columnStyles: {
                0: { cellWidth: this.contentWidth, halign: 'left' }
            },
            tableWidth: this.contentWidth,
            margin: { left: this.margin, right: this.margin }
        });

        pdf.setTextColor(0, 0, 0);
        return pdf.lastAutoTable.finalY + 5; // Further reduced from 6 to save space
    }
    
    /**
     * Payment Mode Detail Page
     */
    addPaymentModeDetailPage(pdf, paymentMode, bookings, invoiceData, pageNumber, paginationInfo = null) {
        const { invoice_info, company_info } = invoiceData;
        let yPos = this.margin;
        
        // Page Header with pagination info
        yPos = this.addDetailPageHeader(pdf, paymentMode, invoice_info, yPos, paginationInfo);
        
        // Shipment Details Table
        yPos = this.addShipmentDetailsTable(pdf, bookings, yPos);
        
        // Payment Mode Summary - only show on the last chunk
        if (!paginationInfo || paginationInfo.isLastChunk) {
            yPos = this.addPaymentModeSummaryBox(pdf, paymentMode, bookings, invoiceData, yPos);
        }
        
        // Footer
        this.addFooterNote(pdf, pageNumber);
    }
    
    /**
     * Detail Page Header
     */
    addDetailPageHeader(pdf, paymentMode, invoice_info, yPos, paginationInfo = null) {
        // Page title with payment mode
        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 102, 204);
        
        let title = `SHIPMENT DETAILS - ${paymentMode.toUpperCase()}`;
        
        // Add pagination info to title if available
        if (paginationInfo && paginationInfo.totalChunks > 1) {
            title += ` (Page ${paginationInfo.chunkIndex + 1} of ${paginationInfo.totalChunks})`;
        }
        
        const titleWidth = pdf.getTextWidth(title);
        const centerX = this.pageWidth / 2;
        
        pdf.text(title, centerX - (titleWidth / 2), yPos + 8);
        
        // Invoice reference
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        
        let reference = `Invoice: ${invoice_info?.invoice_no || ''} | Date: ${this.formatDateForPDF(invoice_info?.invoice_date) || ''}`;
        
        // Add records info if pagination is active
        if (paginationInfo && paginationInfo.totalChunks > 1) {
            reference += ` | Records ${paginationInfo.chunkIndex * 6 + 1}-${paginationInfo.chunkIndex * 6 + paginationInfo.recordsInThisChunk} of ${paginationInfo.totalRecords}`;
        }
        
        const refWidth = pdf.getTextWidth(reference);
        pdf.text(reference, centerX - (refWidth / 2), yPos + 16);
        
        // Separator line
        pdf.setLineWidth(0.5);
        pdf.setDrawColor(0, 102, 204);
        pdf.line(this.margin, yPos + 22, this.pageWidth - this.margin, yPos + 22);
        
        pdf.setTextColor(0, 0, 0);
        pdf.setDrawColor(0, 0, 0);
        
        return yPos + 30;
    }
    
    /**
     * Shipment Details Table
     */
    addShipmentDetailsTable(pdf, bookings, yPos) {
        const headers = [
            'AWB No. & Ref No.',
            'Date',
            'From Address',
            'To Address', 
            'Weight/Pcs',
            'Charges Breakdown',
            'Amount (Rs.)',
            'Delivery Info'
        ];
        
        const tableData = [];
        
        bookings.forEach(item => {
            const booking = item.booking_data;
            const charges = item.charges;
            const deliveryInfo = item.delivery_info;
            
            // Format addresses (shorter format for better fit)
            const fromAddress = this.formatCompactAddress(booking, 'from');
            const toAddress = this.formatCompactAddress(booking, 'to');
            
            // Format weight/pieces
            const weightInfo = `${booking?.charged_wt || 0} kg\n${booking?.no_of_pack || 1} pcs`;
            
            // Format enhanced charges breakdown with discount and GST
            const chargesBreakdown = this.formatEnhancedChargesBreakdown(charges);
            
            // Calculate discount (if any) - use the discount_amount from invoice item
            const discount = charges?.discount_amount || 0;
            
            // Format delivery information
            let deliveryInfoText = '';
            if (deliveryInfo) {
                // Try different approaches for delivery datetime
                let deliveryDateTime = '';
                if (deliveryInfo.delivery_datetime) {
                    deliveryDateTime = this.formatDateTime(deliveryInfo.delivery_datetime);
                } else if (deliveryInfo.delivery_date && deliveryInfo.delivery_time) {
                    deliveryDateTime = this.formatDateTime(`${deliveryInfo.delivery_date} ${deliveryInfo.delivery_time}`);
                } else {
                    deliveryDateTime = 'Date not available';
                }
                
                deliveryInfoText = `Delivery: ${deliveryDateTime}\n`;
                if (deliveryInfo.receiver_name) {
                    deliveryInfoText += `Receiver: ${deliveryInfo.receiver_name}\n`;
                }
                if (deliveryInfo.is_sealed_signed) {
                    deliveryInfoText += 'Seal & Sign: Yes';
                }
            }
            
            // Format AWB with reference number
            const awbWithRef = this.formatAwbWithRef(item.awb_no, booking?.sa_no);
            
            tableData.push([
                awbWithRef,
                this.formatDate(booking?.booking_date) || '',
                fromAddress,
                toAddress,
                weightInfo,
                chargesBreakdown,
                this.formatCurrency(charges?.grand_total || 0),
                deliveryInfoText
            ]);
        });
        
        autoTable(pdf, {
            head: [headers],
            body: tableData,
            startY: yPos,
            styles: {
                fontSize: 7,
                cellPadding: 2,
                lineColor: [200, 200, 200],
                lineWidth: 0.3,
                valign: 'top',
                overflow: 'linebreak'
            },
            headStyles: {
                fillColor: [0, 102, 204],
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                fontSize: 8
            },
            alternateRowStyles: {
                fillColor: [248, 249, 250]
            },
            columnStyles: {
                0: { cellWidth: 22, halign: 'center' },   // AWB No + Ref = 22mm (increased from 18mm)
                1: { cellWidth: 14, halign: 'center' },   // Date = 14mm
                2: { cellWidth: 28, halign: 'left' },     // From Address = 28mm (reduced from 30mm)
                3: { cellWidth: 28, halign: 'left' },     // To Address = 28mm (reduced from 30mm)
                4: { cellWidth: 14, halign: 'center' },   // Weight/Pcs = 14mm
                5: { cellWidth: 32, halign: 'left' },     // Charges Breakdown = 32mm
                6: { cellWidth: 22, halign: 'right', fontStyle: 'bold' },  // Amount = 22mm
                7: { cellWidth: 20, halign: 'left' }      // Delivery Info = 20mm
            },
            // Total = 22+14+28+28+14+32+22+20 = 180mm (within contentWidth)
            tableWidth: this.contentWidth,
            margin: { left: this.margin, right: this.margin }
        });
        
        return pdf.lastAutoTable.finalY + 15;
    }
    
    /**
     * Format compact address for detail pages (shorter format)
     */
    formatCompactAddress(booking, type) {
        const prefix = type === 'from' ? 'frm_' : 'to_';
        let address = '';
        
        // Name only (most important)
        if (booking[`${prefix}name`]) {
            address += booking[`${prefix}name`] + '\n';
        }
        
        // Company if different from name
        if (booking[`${prefix}company`] && booking[`${prefix}company`] !== booking[`${prefix}name`]) {
            address += booking[`${prefix}company`] + '\n';
        }
        
        // City and pincode (most important location info) - handle object values
        const cityRaw = booking[`${prefix}city`];
        const relatedCity = booking[type === 'from' ? 'from_city' : 'to_city'];
        const cityName = this.resolveCityName(cityRaw) || this.resolveCityName(relatedCity);
        const pincode = booking[`${prefix}pincode`] || '';
        if (cityName && pincode) {
            address += `${cityName}-${pincode}\n`;
        } else if (cityName) {
            address += `${cityName}\n`;
        }
        
        // State
        if (booking[`${prefix}state`]) {
            address += booking[`${prefix}state`] + '\n';
        }
        
        // Contact (shortened)
        if (booking[`${prefix}mobile`]) {
            address += booking[`${prefix}mobile`];
        } else if (booking[`${prefix}tel`]) {
            address += booking[`${prefix}tel`];
        }
        
        // Add booking date at the very bottom for from address
        if (type === 'from' && booking?.booking_date) {
            // Format booking date and time on the same line
            const bookingDateTime = this.formatDateTime(booking.booking_date);
            
            // Ensure the booking date is on a single line with proper spacing
            address += '\n\nBooking: ' + bookingDateTime.replace(/\n/g, ' ');
        }
        
        return address.trim() || 'Address N/A';
    }

    // Resolve city when it can be a string or object
    resolveCityName(value) {
        if (!value) return '';
        if (typeof value === 'string' || typeof value === 'number') return String(value);
        if (typeof value === 'object') {
            return value.city_name || value.name || value.title || '';
        }
        return '';
    }
    
    /**
     * Format enhanced charges breakdown with discount and GST
     */
    formatEnhancedChargesBreakdown(charges) {
        let breakdown = '';
        
        // Show discounts explicitly with percentage
        // Mapping correction: basic_freight = original; actual_basic_freight = discounted
        if (charges?.basic_freight && charges.basic_freight > charges?.actual_basic_freight) {
            const original = charges.basic_freight || 0;
            const discounted = charges.actual_basic_freight || 0;
            const discountAmount = (typeof charges.discount_amount === 'number' && !isNaN(charges.discount_amount)) ? charges.discount_amount : Math.max(original - discounted, 0);
            const pct = original > 0 ? ((discountAmount / original) * 100) : 0;
            breakdown += `Original Freight: Rs.${this.formatCurrency(original)}\n`;
            breakdown += `Discount (${pct.toFixed(2)}%): Rs. -${this.formatCurrency(discountAmount)}\n`;
            breakdown += `Net Freight: Rs.${this.formatCurrency(discounted)}\n`;
        } else {
            // No discount, show regular freight
            if (charges?.actual_basic_freight) {
                breakdown += `Freight: Rs.${this.formatCurrency(charges.actual_basic_freight)}\n`;
            }
        }
        
        // Fuel surcharge
        if (charges?.fuel_surcharge) {
            breakdown += `Fuel: Rs.${this.formatCurrency(charges.fuel_surcharge)}\n`;
        }
        
        // Handling charges
        if (charges?.handling_charges) {
            breakdown += `Handling: Rs.${this.formatCurrency(charges.handling_charges)}\n`;
        }
        
        // GST breakdown (split into SGST 9% and CGST 9%)
        const totalGST = charges?.ser_tax || 0;
        if (totalGST > 0) {
            const sgst = totalGST / 2;
            const cgst = totalGST / 2;
            breakdown += `SGST 9%: Rs.${this.formatCurrency(sgst)}\n`;
            breakdown += `CGST 9%: Rs.${this.formatCurrency(cgst)}\n`;
        }
        
        // Other charges breakdown
        const otherCharges = [
            { name: 'Statutory', value: charges?.stat_charges || 0 },
            { name: 'To Pay Surcharge', value: charges?.to_pay_surcharge || 0 },
            { name: 'COD Charges', value: charges?.cod_charges || 0 },
            { name: 'Special Services', value: charges?.sps_charges || 0 },
            { name: 'Value Surcharge', value: charges?.value_surc || 0 },
            { name: 'CR Charges', value: charges?.cr_charges || 0 },
            { name: 'Remote Area Charges', value: charges?.remote_area_charges || 0 }
        ];
        
        otherCharges.forEach(charge => {
            if (charge.value > 0) {
                breakdown += `${charge.name}: Rs.${this.formatCurrency(charge.value)}\n`;
            }
        });
        
        // Remove summary block per requirement; we already show discount above.
        
        return breakdown.trim() || 'No breakdown available';
    }
    
    /**
     * Payment Mode Summary Box
     */
    addPaymentModeSummaryBox(pdf, paymentMode, bookings, invoiceData, yPos) {
        // Get all bookings for this payment mode from the invoice data
        const { booking_details } = invoiceData;
        const allBookingsForPaymentMode = booking_details[paymentMode] || [];
        
        // Calculate totals for this payment mode (all bookings, not just current chunk)
        let totalShipments = allBookingsForPaymentMode.length;
        let totalFreight = 0;
        let totalFuel = 0;
        let totalDiscount = 0;
        let totalOther = 0;
        let totalGST = 0;
        let grandTotal = 0;
        
        allBookingsForPaymentMode.forEach(item => {
            const charges = item.charges;
            totalFreight += charges?.basic_freight || 0;
            totalFuel += charges?.fuel_surcharge || 0;
            
            // Calculate discount - use the discount_amount from invoice item
            totalDiscount += charges?.discount_amount || 0;
            
            // Calculate total GST (displayed as SGST 9% + CGST 9% = 18% total)
            totalGST += charges?.ser_tax || 0;
            
            totalOther += (charges?.handling_charges || 0) + 
                         (charges?.stat_charges || 0) + 
                         (charges?.to_pay_surcharge || 0) + 
                         (charges?.cod_charges || 0) + 
                         (charges?.sps_charges || 0) + 
                         (charges?.value_surc || 0) + 
                         (charges?.cr_charges || 0) + 
                         (charges?.remote_area_charges || 0);
            grandTotal += charges?.total_amt || 0;
        });
        
        // Summary box
        pdf.setFillColor(240, 248, 255);
        const boxHeight = 40;
        pdf.rect(this.margin, yPos, this.contentWidth, boxHeight, 'F');
        
        pdf.setLineWidth(0.5);
        pdf.setDrawColor(0, 102, 204);
        pdf.rect(this.margin, yPos, this.contentWidth, boxHeight);
        
        // Title
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 102, 204);
        pdf.text(`${paymentMode.toUpperCase()} - SUMMARY`, this.margin + 5, yPos + 8);
        
        // Summary details in two columns
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        
        const leftCol = this.margin + 5;
        const rightCol = this.margin + (this.contentWidth / 2) + 5;
        
        let summaryY = yPos + 15;
        
        // Left column
        pdf.text(`Total Shipments: ${totalShipments}`, leftCol, summaryY);
        pdf.text(`Total Freight: Rs.${this.formatCurrency(totalFreight)}`, leftCol, summaryY + 4);
        pdf.text(`Fuel Surcharge: Rs.${this.formatCurrency(totalFuel)}`, leftCol, summaryY + 8);
        if (totalDiscount > 0) {
            pdf.text(`Total Discount: Rs.${this.formatCurrency(totalDiscount)}`, leftCol, summaryY + 12);
        }
        
        // Right column
        const sgstAmount = totalGST / 2;
        const cgstAmount = totalGST / 2;
        pdf.text(`SGST 9%: Rs.${this.formatCurrency(sgstAmount)}`, rightCol, summaryY);
        pdf.text(`CGST 9%: Rs.${this.formatCurrency(cgstAmount)}`, rightCol, summaryY + 4);
        pdf.text(`Other Charges: Rs.${this.formatCurrency(totalOther)}`, rightCol, summaryY + 8);
        
        // Grand total (centered)
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        const totalText = `TOTAL AMOUNT: Rs.${this.formatCurrency(Math.round(grandTotal + totalGST))}`;
        const totalWidth = pdf.getTextWidth(totalText);
        const centerX = this.margin + (this.contentWidth / 2);
        pdf.text(totalText, centerX - (totalWidth / 2), summaryY + 19); // Adjusted from 15 to 19 to account for extra line
        
        pdf.setTextColor(0, 0, 0);
        pdf.setDrawColor(0, 0, 0);
        
        return yPos + boxHeight + 10;
    }
    
    /**
     * Add page numbers to all pages
     */
    addPageNumbers(pdf) {
        const pageCount = pdf.internal.getNumberOfPages();
        
        for (let i = 1; i <= pageCount; i++) {
            pdf.setPage(i);
            this.addFooterNote(pdf, i, pageCount);
        }
    }
    
    /**
     * Footer note for each page
     */
    addFooterNote(pdf, pageNumber, totalPages = null) {
        const footerY = this.pageHeight - 15;

        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'italic');
        pdf.setTextColor(100, 100, 100);

        // Page number (only show if there are multiple pages)
        if (totalPages && totalPages > 1) {
            const pageText = `Page ${pageNumber} of ${totalPages}`;
            pdf.text(pageText, this.pageWidth - this.margin - 20, footerY);
        }

        pdf.setTextColor(0, 0, 0);
    }
    
    /**
     * Helper Methods
     */
    parseCustomerData(htmlData) {
        if (!htmlData) return '';
        
        // Remove HTML tags and format for PDF
        return htmlData
            .replace(/<br\/>/gi, '\n')
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/g, ' ')
            .trim();
    }
    
    formatCurrency(amount) {
        if (!amount || isNaN(amount)) return '0.00';
        const numAmount = parseFloat(amount);
        return numAmount.toLocaleString('en-IN', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
        });
    }
    
    formatDate(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB');
    }

    formatDateTime(dateTimeString) {
        if (!dateTimeString) return '';
        
        try {
            // Handle different date formats that might come from backend
            let date;
            
            // If it's already a combined string like "2024-01-15 14:30:00"
            if (typeof dateTimeString === 'string' && dateTimeString.includes(' ')) {
                // Handle malformed datetime strings like "2025-08-06 00:00:00 00:12:00"
                const parts = dateTimeString.split(' ');
                
                if (parts.length >= 2) {
                    const datePart = parts[0]; // First part is always the date
                    
                    // Find the actual time part (skip the first time if there are multiple)
                    let timePart;
                    if (parts.length === 2) {
                        // Normal format: "2025-08-06 00:12:00"
                        timePart = parts[1];
                    } else if (parts.length >= 3) {
                        // Malformed format: "2025-08-06 00:00:00 00:12:00"
                        // Use the last time part as it's likely the actual delivery time
                        timePart = parts[parts.length - 1];
                    }
                    
                    // Ensure proper format for JavaScript Date constructor
                    const formattedDateTime = `${datePart}T${timePart}`;
                    date = new Date(formattedDateTime);
                } else {
                    date = new Date(dateTimeString);
                }
            } 
            // If it's separate date and time, combine them
            else if (typeof dateTimeString === 'object' && dateTimeString.date && dateTimeString.time) {
                const formattedDateTime = `${dateTimeString.date}T${dateTimeString.time}`;
                date = new Date(formattedDateTime);
            }
            // Otherwise try direct parsing
            else {
                date = new Date(dateTimeString);
            }
            
            // Check if date is valid
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            
            const formattedDate = date.toLocaleDateString('en-GB') + ' ' + date.toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
            
            // Ensure it's a single line without any line breaks
            return formattedDate.replace(/\n/g, ' ').trim();
        } catch (error) {
            console.error('Error formatting date:', dateTimeString, error);
            return 'Invalid Date';
        }
    }
    
    /**
     * Download PDF with professional filename format
     */
    downloadPDF(pdf, invoiceNumber) {
        const filename = `SunEx_Invoice_${invoiceNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
        pdf.save(filename);
    }
    
    /**
     * Open PDF in print preview for immediate printing
     */
    openPrintPreview(pdf, invoiceNumber) {
        const filename = `SunEx_Invoice_${invoiceNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
        
        // Create blob and open in new window for print preview
        const pdfBlob = pdf.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        
        // Open in new window optimized for printing
        const printWindow = window.open(pdfUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
        
        if (printWindow) {
            printWindow.onload = function() {
                // Add a small delay to ensure PDF loads completely
                setTimeout(() => {
                    printWindow.focus();
                    printWindow.print();
                }, 500);
            };
        } else {
            // Fallback if popup blocked - download instead
            this.downloadPDF(pdf, invoiceNumber);
        }
    }
    
    /**
     * Get print-optimized blob for direct printing
     */
    getPrintBlob(pdf) {
        return pdf.output('blob', {
            compress: false // Don't compress for better print quality
        });
    }
    
    /**
     * Test autoTable functionality
     */
    testAutoTable() {
        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            
            // Simple test table
            autoTable(pdf, {
                head: [['Test', 'Column']],
                body: [['Test', 'Data']],
                startY: 20
            });
            
            return { success: true, message: 'AutoTable is working correctly' };
        } catch (error) {
            console.error('AutoTable test failed:', error);
            return { success: false, message: error.message };
        }
    }

    /**
     * Helper function to split array into chunks
     */
    chunkArray(array, chunkSize) {
        const chunks = [];
        for (let i = 0; i < array.length; i += chunkSize) {
            chunks.push(array.slice(i, i + chunkSize));
        }
        return chunks;
    }

    /**
     * Test pagination logic for various total counts
     * This method helps verify that the smart pagination works correctly
     */
    testPaginationLogic(totalRecords) {
        let bookingChunks = [];
        
        if (totalRecords <= 6) {
            // 6 or fewer records - all on one page with summary
            bookingChunks = [Array.from({length: totalRecords}, (_, i) => i + 1)];
        } else if (totalRecords === 7) {
            // Special case: exactly 7 records - split into 6 + 1 for summary space
            bookingChunks = [
                Array.from({length: 6}, (_, i) => i + 1),  // First 6 records
                [7]   // Last 1 record (will have summary)
            ];
        } else {
            // More than 7 records - smart pagination to ensure last page has ≤ 6 records
            const recordsPerPage = 7;
            const fullPages = Math.floor(totalRecords / recordsPerPage);
            const remainingRecords = totalRecords % recordsPerPage;
            
            bookingChunks = [];
            
            if (remainingRecords === 0) {
                // Perfect division (e.g., 14, 21, 28, 35, etc.)
                // Need to ensure last page has ≤ 6 records
                const adjustedFullPages = fullPages - 1; // Reduce by 1 to leave space for summary
                
                // Add full pages (7 records each)
                for (let i = 0; i < adjustedFullPages; i++) {
                    const start = i * recordsPerPage;
                    bookingChunks.push(Array.from({length: 7}, (_, j) => start + j + 1));
                }
                
                // Add second-to-last page with 6 records
                const secondLastPageStart = adjustedFullPages * recordsPerPage;
                bookingChunks.push(Array.from({length: 6}, (_, j) => secondLastPageStart + j + 1));
                
                // Add last page with remaining records
                const lastPageStart = secondLastPageStart + 6;
                const remainingInLastPage = totalRecords - lastPageStart;
                if (remainingInLastPage > 0) {
                    bookingChunks.push(Array.from({length: remainingInLastPage}, (_, j) => lastPageStart + j + 1));
                }
            } else {
                // Has remaining records (e.g., 8, 9, 15, 16, 22, 23, etc.)
                
                // Add full pages
                for (let i = 0; i < fullPages; i++) {
                    const start = i * recordsPerPage;
                    bookingChunks.push(Array.from({length: 7}, (_, j) => start + j + 1));
                }
                
                // Handle remaining records
                if (remainingRecords <= 6) {
                    // 6 or fewer remaining - put them on last page with summary
                    const start = fullPages * recordsPerPage;
                    bookingChunks.push(Array.from({length: remainingRecords}, (_, j) => start + j + 1));
                } else {
                    // Exactly 7 remaining - split into 6 + 1
                    const start = fullPages * recordsPerPage;
                    bookingChunks.push(Array.from({length: 6}, (_, j) => start + j + 1));
                    bookingChunks.push([start + 7]);
                }
            }
        }
        
        return {
            totalRecords,
            totalPages: bookingChunks.length,
            pages: bookingChunks.map((chunk, index) => ({
                pageNumber: index + 1,
                recordCount: chunk.length,
                records: chunk,
                hasSummary: index === bookingChunks.length - 1 // Only last page has summary
            })),
            lastPageRecordCount: bookingChunks[bookingChunks.length - 1]?.length || 0,
            summaryVisible: bookingChunks[bookingChunks.length - 1]?.length <= 6
        };
    }
    
    /**
     * Test freight and other charges consolidation to ensure totals remain accurate
     */
    testFreightConsolidation() {
        console.log('=== Testing Freight + Other Charges Consolidation ===');

        // Test with sample data
        const testData = [
            { freight: 100, other_charges: 25, expected_combined: 125 },
            { freight: 250.50, other_charges: 34.75, expected_combined: 285.25 },
            { freight: 0, other_charges: 15.50, expected_combined: 15.50 },
            { freight: 300, other_charges: 0, expected_combined: 300 },
            { freight: 999.99, other_charges: 100.01, expected_combined: 1100.00 }
        ];

        testData.forEach((test, index) => {
            const combinedFreight = (test.freight || 0) + (test.other_charges || 0);

            console.log(`Test ${index + 1}:`);
            console.log(`  Freight: ${test.freight}`);
            console.log(`  Other Charges: ${test.other_charges}`);
            console.log(`  Combined: ${combinedFreight.toFixed(2)}`);
            console.log(`  Expected: ${test.expected_combined.toFixed(2)}`);
            console.log(`  Match: ${Math.abs(combinedFreight - test.expected_combined) < 0.01 ? '✅ YES' : '❌ NO'}`);
            console.log('---');
        });

        console.log('=== Freight Consolidation Test Complete ===');
    }

    /**
     * Test GST split calculations to ensure totals remain accurate
     */
    testGSTSplitCalculations() {
        console.log('=== Testing GST Split Calculations ===');

        // Test various GST amounts
        const testAmounts = [100, 150.50, 234.75, 999.99, 1234.56];

        testAmounts.forEach(totalGST => {
            const sgst = totalGST / 2;
            const cgst = totalGST / 2;
            const recombined = sgst + cgst;

            console.log(`Total GST: ${totalGST}`);
            console.log(`SGST 9%: ${sgst.toFixed(2)}`);
            console.log(`CGST 9%: ${cgst.toFixed(2)}`);
            console.log(`Recombined: ${recombined.toFixed(2)}`);
            console.log(`Match: ${Math.abs(totalGST - recombined) < 0.01 ? '✅ YES' : '❌ NO'}`);
            console.log('---');
        });

        console.log('=== GST Split Test Complete ===');
    }

    /**
     * Test the pagination logic with various total counts
     * This helps verify that the smart pagination works correctly for all scenarios
     */
    testPaginationWithVariousCounts() {
        const testCases = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 13, 14, 15, 20, 21, 27, 28, 29, 35, 42, 49, 50];
        
        console.log('=== Testing Smart Pagination Logic ===');
        
        testCases.forEach(totalRecords => {
            const result = this.testPaginationLogic(totalRecords);
            console.log(`\nTotal Records: ${totalRecords}`);
            console.log(`Total Pages: ${result.totalPages}`);
            console.log(`Last Page Records: ${result.lastPageRecordCount}`);
            console.log(`Summary Visible: ${result.summaryVisible ? '✅ YES' : '❌ NO'}`);
            
            if (!result.summaryVisible) {
                console.log('❌ ISSUE: Summary will not be visible!');
            }
            
            console.log('Pages:', result.pages.map(p => `${p.pageNumber}:${p.recordCount} records`).join(', '));
        });
        
        console.log('\n=== Test Complete ===');
    }

    /**
     * Main method to generate and download professional multi-page invoice
     */
    async generateAndDownloadProfessionalInvoice(invoiceData, action = 'download') {
        try {
            // Test autoTable first
            const tableTest = this.testAutoTable();
            if (!tableTest.success) {
                throw new Error('AutoTable functionality not available: ' + tableTest.message);
            }

            const pdf = await this.generateLegacyInvoicePDF(invoiceData);
            
            // Handle different actions
            switch (action) {
                case 'print':
                    this.openPrintPreview(pdf, invoiceData.invoice_info?.invoice_no || 'INVOICE');
                    break;
                case 'download':
                default:
                    this.downloadPDF(pdf, invoiceData.invoice_info?.invoice_no || 'INVOICE');
                    break;
            }
            
            return {
                success: true,
                message: `Professional multi-page invoice ${action === 'print' ? 'opened for printing' : 'downloaded'} successfully`
            };
        } catch (error) {
            console.error('Error generating professional invoice:', error);
            return {
                success: false,
                message: 'Failed to generate PDF: ' + error.message
            };
        }
    }

    /**
     * Generate and print invoice directly
     */
    async generateAndPrintProfessionalInvoice(invoiceData) {
        return this.generateAndDownloadProfessionalInvoice(invoiceData, 'print');
    }

    /**
     * Backward compatibility: Legacy method name
     */
    async generateAndDownloadLegacyInvoice(invoiceData) {
        return this.generateAndDownloadProfessionalInvoice(invoiceData);
    }

    /**
     * Generate the invoice PDF as a blob for emailing, using the same renderer
     * as download/print so the customer receives an identical document.
     */
    async generateInvoicePdfBlob(invoiceData) {
        try {
            const tableTest = this.testAutoTable();
            if (!tableTest.success) {
                throw new Error('AutoTable functionality not available: ' + tableTest.message);
            }

            const pdf = await this.generateLegacyInvoicePDF(invoiceData);
            const invoiceNumber = invoiceData.invoice_info?.invoice_no || 'INVOICE';

            return {
                success: true,
                blob: pdf.output('blob'),
                // Invoice numbers contain slashes; keep the filename safe.
                filename: `SunEx_Invoice_${String(invoiceNumber).replace(/[\\/]/g, '-')}.pdf`
            };
        } catch (error) {
            console.error('Error generating invoice PDF blob:', error);
            return {
                success: false,
                message: 'Failed to generate PDF: ' + error.message
            };
        }
    }

    /**
     * Generate Delivery Run Sheet PDF with pagination (10 records per page)
     */
    async generateDeliveryRunSheetPDF(runSheetData) {
        // Load logo first
        await this.loadLogo();
        
        // Create new PDF document
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            putOnlyUsedFonts: true,
            floatPrecision: 16
        });
        
        // Set PDF properties
        pdf.setFont('helvetica');
        pdf.setCreationDate(new Date());
        pdf.setProperties({
            title: `Delivery Run Sheet - ${runSheetData.delivery_date}`,
            subject: 'Delivery Run Sheet',
            author: 'SUN-EX COURIER, CARGO & LOGISTICS PVT. LTD.',
            creator: 'SunEx Delivery System',
            producer: 'jsPDF'
        });
        
        // Split bookings into chunks of 10 records per page
        const recordsPerPage = 10;
        const bookingChunks = this.chunkArray(runSheetData.bookings, recordsPerPage);
        const totalPages = bookingChunks.length;
        
        // Generate each page
        for (let pageIndex = 0; pageIndex < bookingChunks.length; pageIndex++) {
            const currentChunk = bookingChunks[pageIndex];
            const currentPage = pageIndex + 1;
            
            // Add new page for subsequent chunks
            if (pageIndex > 0) {
                pdf.addPage();
            }
            
            let yPos = this.margin;
            
            // Add header with company logo and info
            yPos = this.addDeliveryRunSheetHeader(pdf, runSheetData, yPos, currentPage, totalPages);
            
            // Create page-specific data
            const pageData = {
                ...runSheetData,
                bookings: currentChunk,
                currentPage: currentPage,
                totalPages: totalPages
            };
            
            // Add delivery run sheet table for this page
            yPos = this.addDeliveryRunSheetTable(pdf, pageData, yPos);
            
            // Add footer for this page
            this.addDeliveryRunSheetFooter(pdf, pageData, currentPage, totalPages);
        }
        
        return pdf;
    }

    /**
     * Add Delivery Run Sheet Header with page information
     */
    addDeliveryRunSheetHeader(pdf, runSheetData, yPos, currentPage = 1, totalPages = 1) {
        // Company Logo (if available)
        if (this.logoBase64) {
            try {
                const logoWidth = 60;
                const logoHeight = 25;
                pdf.addImage(this.logoBase64, 'PNG', this.margin, yPos, logoWidth, logoHeight);
            } catch (error) {
                // Logo error silently ignored
            }
        }
        
        // Delivery Run Sheet Title and Date (Right side)
        const titleX = this.pageWidth - this.margin - 80;
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 102, 204); // Blue color
        
        const title = 'DELIVERY RUN SHEET';
        const titleLines = pdf.splitTextToSize(title, 80);
        
        let textY = yPos + 8;
        titleLines.forEach(line => {
            pdf.text(line, titleX, textY);
            textY += 6;
        });
        
        // Date and extra info
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        const dateText = `Date: ${this.formatDate(runSheetData.delivery_date)}`;
        pdf.text(dateText, titleX, textY + 2);
        
        // DRS Information (if available)
        if (runSheetData.drs_header) {
            pdf.text(`DRS No: ${runSheetData.drs_header.drs_no}`, titleX, textY + 8);
        }
        
        if (runSheetData.branch_name && runSheetData.branch_name.trim()) {
            pdf.text(`Branch: ${runSheetData.branch_name}`, titleX, textY + (runSheetData.drs_header ? 14 : 8));
        }
        if (runSheetData.area_locator && runSheetData.area_locator.trim()) {
            pdf.text(`Area/Locator: ${runSheetData.area_locator}`, titleX, textY + (runSheetData.drs_header ? 20 : 14));
        }
        
        // Reset text color
        pdf.setTextColor(0, 0, 0);
        
        // Calculate height based on whether DRS header info is present
        const extraHeight = runSheetData.drs_header ? 30 : 25;
        return yPos + extraHeight;
    }
    
    /**
     * Add Delivery Run Sheet Table
     */
    addDeliveryRunSheetTable(pdf, runSheetData, yPos) {
        // Add staff, driver, vehicle information just above the table if available
        if (runSheetData.drs_header) {
            const staffInfo = [];
            
            if (runSheetData.drs_header.staff_name && runSheetData.drs_header.staff_name.trim()) {
                staffInfo.push(`Staff: ${runSheetData.drs_header.staff_name}`);
            }
            if (runSheetData.drs_header.driver_name && runSheetData.drs_header.driver_name.trim()) {
                staffInfo.push(`Driver: ${runSheetData.drs_header.driver_name}`);
            }
            if (runSheetData.drs_header.vehicle_no && runSheetData.drs_header.vehicle_no.trim()) {
                staffInfo.push(`Vehicle: ${runSheetData.drs_header.vehicle_no}`);
            }
            
            if (staffInfo.length > 0) {
                pdf.setFontSize(10);
                pdf.setFont('helvetica', 'bold');
                pdf.setTextColor(0, 102, 204); // Blue color
                
                const staffText = staffInfo.join(' | ');
                pdf.text(staffText, this.margin, yPos + 5);
                
                // Add a line below the staff info
                pdf.setDrawColor(0, 102, 204);
                pdf.setLineWidth(0.5);
                pdf.line(this.margin, yPos + 7, this.pageWidth - this.margin, yPos + 7);
                
                // Reset text color and move yPos down
                pdf.setTextColor(0, 0, 0);
                yPos += 12; // Add space for staff info and line
            }
        }
        
        // Pre-process booking data with barcode information for efficient rendering
        const processedBookings = runSheetData.bookings.map((booking, index) => {
            
            return {
                ...booking,
                // Ensure consistent indexing
                processedIndex: index,
                // Pre-validate barcode data
                validBarcode: booking.booking_qr && booking.booking_qr.trim() !== '',
                // Prepare barcode data URL if needed
                barcodeDataUrl: booking.booking_qr ? 
                    (booking.booking_qr.startsWith('data:') ? booking.booking_qr : `data:image/png;base64,${booking.booking_qr}`) 
                    : null
            };
        });
        
        // Create table data with embedded booking reference for efficient lookup
        const tableData = processedBookings.map((processedBooking) => {
            return [
                processedBooking.si_no, // Use the same SI.NO as HTML table
                // Use autoTable's cell object format with custom properties
                {
                    content: processedBooking.awb_no, // Display text
                    styles: { halign: 'center', valign: 'top' }, // autoTable styling
                    bookingRef: processedBooking // Custom property for our use
                },
                `${processedBooking.from_customer}\n${processedBooking.from_city}`, // From
                `${processedBooking.to_customer}\n${processedBooking.to_city}`, // To
                processedBooking.no_of_packages, // Packages
                processedBooking.weight, // Weight
                '' // Seal & Sign (empty for manual signing)
            ];
        });
        
        // Table headers
        const headers = [
            'SI.NO',
            'AWB No & Barcode',
            'Consignor',
            'Consignee', 
            'Packages',
            'Weight (kg)',
            'Seal & Sign'
        ];
        
        // Calculate available space for table (leave 35mm for footer)
        const footerMargin = 35; // Increased margin for footer
        const maxTableHeight = this.pageHeight - yPos - footerMargin;
        
        // Table configuration with proper page break handling
        const tableConfig = {
            head: [headers],
            body: tableData,
            startY: yPos,
            styles: {
                fontSize: 8,
                cellPadding: 3, // Reduced padding for better fit
                lineColor: [70, 130, 180], // Lighter blue for borders
                lineWidth: 0.3, // Thinner lines
                textColor: [0, 0, 0],
                halign: 'center',
                valign: 'top', // Align content to top for better barcode positioning
                minCellHeight: 18 // Reduced height for better page fit
            },
            // Prevent table from extending into footer area
            pageBreak: 'auto',
            rowPageBreak: 'avoid',
            pageBreakBefore: function(cursor, doc) {
                return cursor.y >= doc.pageSize.height - footerMargin;
            },
            headStyles: {
                fillColor: [135, 206, 235], // Light pleasant blue for header
                textColor: [0, 0, 0], // Black text on light blue
                fontStyle: 'bold',
                fontSize: 9,
                halign: 'center',
                valign: 'middle'
            },
            columnStyles: {
                0: { cellWidth: 14, halign: 'center' }, // SI.NO - centered
                1: { cellWidth: 32, halign: 'center', valign: 'top' }, // AWB No & Barcode - centered, top aligned
                2: { cellWidth: 32, halign: 'left' }, // From - left aligned
                3: { cellWidth: 32, halign: 'left' }, // To - left aligned
                4: { cellWidth: 15, halign: 'center' }, // Packages - centered
                5: { cellWidth: 15, halign: 'center' }, // Weight - centered
                6: { cellWidth: 30, halign: 'center' }  // Seal & Sign - centered
            },
            alternateRowStyles: {
                fillColor: [248, 249, 250]
            },
            margin: { left: this.margin, right: this.margin },
            didDrawCell: function(data) {
                // Add barcode to AWB No & Barcode column (column 1)
                if (data.column.index === 1) { // Skip header row
                    // Get the booking reference from autoTable cell object (no lookup needed!)
                    const cellData = data.cell.raw;
                    const booking = cellData && typeof cellData === 'object' && cellData.bookingRef ? cellData.bookingRef : null;
                    
                    if (!booking) {
                        console.error('No booking reference found in cell data:', cellData);
                        return;
                    }
                    
                    try {
                        // Clear the cell content area
                        pdf.setFillColor(255, 255, 255);
                        pdf.rect(data.cell.x + 1, data.cell.y + 1, data.cell.width - 2, data.cell.height - 2, 'F');
                        
                        // Position AWB number at the top of cell
                        pdf.setFontSize(8);
                        pdf.setFont('helvetica', 'normal');
                        pdf.setTextColor(0, 0, 0);
                        
                        const awbX = data.cell.x + data.cell.width / 2;
                        const awbY = data.cell.y + 5; // Slightly higher for reduced cell height
                        
                        // Add AWB number at top center
                        pdf.text(booking.awb_no, awbX, awbY, { align: 'center' });
                        
                        // Add barcode if available
                        if (booking.validBarcode && booking.barcodeDataUrl) {
                            // Position barcode below AWB number (adjusted for smaller cell)
                            const barcodeX = data.cell.x + (data.cell.width / 2) - 11; // Center the barcode
                            const barcodeY = data.cell.y + 8; // Closer to AWB number
                            const barcodeWidth = 22; // Slightly smaller barcode
                            const barcodeHeight = 7; // Reduced height
                            
                            pdf.addImage(
                                booking.barcodeDataUrl,
                                'PNG',
                                barcodeX,
                                barcodeY,
                                barcodeWidth,
                                barcodeHeight
                            );
                        } else {
                            // No barcode available - add placeholder text
                            pdf.setFontSize(6);
                            pdf.setFont('helvetica', 'italic');
                            pdf.setTextColor(150, 150, 150);
                            pdf.text('(No Barcode)', awbX, data.cell.y + 13, { align: 'center' }); // Adjusted for smaller cell
                        }
                    } catch (error) {
                        console.error('Error adding barcode to PDF for AWB ' + booking.awb_no + ':', error);
                        // Fallback: just show AWB number if barcode fails
                        pdf.setFillColor(255, 255, 255);
                        pdf.rect(data.cell.x + 1, data.cell.y + 1, data.cell.width - 2, data.cell.height - 2, 'F');
                        pdf.setFontSize(8);
                        pdf.setTextColor(0, 0, 0);
                        const awbX = data.cell.x + data.cell.width / 2;
                        const awbY = data.cell.y + 9; // Centered in reduced cell height
                        pdf.text(booking.awb_no, awbX, awbY, { align: 'center' });
                    }
                }
            }
        };
        
        autoTable(pdf, tableConfig);
        
        return pdf.lastAutoTable.finalY + 10;
    }
    
    /**
     * Add Delivery Run Sheet Footer with page information
     */
    addDeliveryRunSheetFooter(pdf, runSheetData, currentPage = 1, totalPages = 1) {
        const footerY = this.pageHeight - 25; // Increased margin from bottom
        
        // Footer note
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'italic');
        pdf.setTextColor(100, 100, 100);
        pdf.text('This delivery run sheet is generated by SunEx Courier System', this.margin, footerY - 8);
        pdf.text(`Generated on: ${this.formatDate(new Date().toISOString())}`, this.pageWidth - this.margin - 50, footerY - 8);
        
        // Page-specific summary info (without date)
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        
        // Show records count for this page and total
        const recordsOnThisPage = runSheetData.bookings ? runSheetData.bookings.length : 0;
        const totalRecords = runSheetData.total_bookings || recordsOnThisPage;
        
        let summaryText;
        if (totalPages > 1) {
            summaryText = `Page ${currentPage} of ${totalPages} | Records on this page: ${recordsOnThisPage} | Total Records: ${totalRecords}`;
        } else {
            summaryText = `Total Bookings: ${totalRecords}`;
        }
        
        pdf.text(summaryText, this.margin, footerY - 2);
    }
    
    /**
     * Generate and Download Delivery Run Sheet PDF
     */
    async generateAndDownloadDeliveryRunSheet(runSheetData) {
        const pdf = await this.generateDeliveryRunSheetPDF(runSheetData);
        this.downloadPDF(pdf, `DeliveryRunSheet-${runSheetData.delivery_date}`);
    }

    /**
     * Generate and Print Delivery Run Sheet PDF
     */
    async generateAndPrintDeliveryRunSheet(runSheetData) {
        const pdf = await this.generateDeliveryRunSheetPDF(runSheetData);
        // Use the same print preview logic as invoices
        this.openPrintPreview(pdf, `DeliveryRunSheet-${runSheetData.delivery_date}`);
    }

    /**
     * Format AWB number with reference number at the bottom
     */
    formatAwbWithRef(awbNo, refNo) {
        if (!awbNo) return 'N/A';
        
        let formattedText = awbNo;
        
        // Add reference number at the bottom if it exists
        if (refNo && refNo.trim()) {
            // Add extra line breaks to push reference number to bottom
            formattedText += '\n\n\nRef.No: ' + refNo.trim();
        }
        
        return formattedText;
    }
}

// Create singleton instance
const pdfService = new PDFService();
export default pdfService; 